/* De doelen waarvoor grondwater onttrokken mag worden, op één plek.

   Beleidsregel art. 22.4, 22.6 en 22.8. Deze lijst stond in drie kopieën —
   InrichtingNieuw, InrichtingDetail en WijzigingsbesluitModal — die alle drie
   hetzelfde bevatten en dus alle drie apart zouden verouderen zodra er een
   categorie bij komt.

   Sinds dezelfde ronde staan hier ook de drie doelen van de vrijstelling. Die
   hadden vijf verschillende bewoordingen in omloop: "Graslandberegening" in de
   backend, "Grasland" in het venster van een klaarstaande melding, "Grasland
   voor vee" in het meldingsformulier, en in de inrichtingenlijst helemaal geen
   label maar de kale databasewaarde. Wat de melder op stap 1 heeft gelezen is
   nu wat overal staat. */

/* De opsomming die de vrijstelling geeft voor alles wat geen grasland voor vee
   is. Staat apart omdat hij in twee samenstellingen terugkomt en anders twee
   keer bijgewerkt zou moeten worden. */
const GEWAS = 'akkerbouw, vollegrondstuinbouw, vollegrondsboomteelt, glastuinbouw en container- en substraatteelt';

/* De drie doelen van de vrijstelling, in de bewoording van het
   meldingsformulier. `voluit` is die bewoording letterlijk — dat is wat de
   melder heeft aangevinkt en dus wat er in een keuzelijst, een toelichting of
   een uitdraai hoort te staan. `kort` is de samenvatting voor een tabelcel of
   een chip, waar `voluit` niet past.

   Er is bewust geen derde vorm. Past `voluit` niet, gebruik dan `kort` met
   `voluit` als title, zodat de volledige tekst een muisaanwijzer ver weg is.

   De sleutels blijven grasland/gewas/beide: zo staan ze in de database en in
   de meldings-JSON. */
export const MELDING_DOELEN = [
  {
    waarde: 'grasland',
    pomp: 70,
    voluit: 'Grasland voor vee',
    kort: 'Grasland voor vee',
  },
  {
    waarde: 'gewas',
    pomp: 100,
    voluit: GEWAS.charAt(0).toUpperCase() + GEWAS.slice(1),
    kort: 'Akkerbouw en tuinbouw',
  },
  {
    waarde: 'beide',
    pomp: 100,
    voluit: `Grasland voor vee en ${GEWAS}`,
    kort: 'Grasland voor vee, akkerbouw en tuinbouw',
  },
];

/* De toelichting onder de keuze, gelijk aan de regel die op stap 1 van het
   meldingsformulier onder dezelfde optie staat. */
export const MELDING_DOEL_TOELICHTING = {
  grasland: 'Weidegras of grassen die voor de productie van ruwvoer worden geteeld.',
  gewas: 'Ook hoogwaardige graszoden en nachtvorstberegening.',
  beide: 'Grasland voor vee én alles wat daar niet onder valt.',
};

export const VERGUNNING_CATEGORIEEN = [
  { key: 'agrarisch', label: 'Agrarisch', opties: [
    { value: 'grasland', label: 'Grasland voor vee' },
    { value: 'akkerbouw', label: 'Akkerbouw' },
    { value: 'vollegronds_tuinbouw', label: 'Vollegrondstuinbouw' },
    { value: 'vollegronds_boomteelt', label: 'Vollegrondsboomteelt' },
    { value: 'glastuinbouw', label: 'Glastuinbouw' },
    { value: 'container_substraat', label: 'Container- en substraatteelt' },
  ]},
  { key: 'sport_recreatie', label: 'Sportvelden, recreatie en openbaar groen', opties: [
    { value: 'parken_openbaar_groen', label: 'Parken en openbaar groen' },
    { value: 'golfbanen_recreatie', label: 'Golfbanen en recreatie' },
    { value: 'sportvelden', label: 'Sportvelden' },
  ]},
  { key: 'particulier', label: 'Particulier', opties: [
    { value: 'particuliere_tuin', label: 'Particuliere of hobbytuin' },
  ]},
];

function vergunningLabel(value) {
  for (const cat of VERGUNNING_CATEGORIEEN) {
    const opt = cat.opties.find((o) => o.value === value);
    if (opt) return opt.label;
  }
  return null;
}

/* Het korte label bij een opgeslagen waarde: voor tabellen, chips en de
   gegevensvelden in de vensters. De melding-doelen gaan vóór, want `grasland`
   bestaat in beide lijsten en betekent hetzelfde. */
export function labelVoorDoel(value) {
  const d = MELDING_DOELEN.find((o) => o.waarde === value);
  if (d) return d.kort;
  return vergunningLabel(value) || value;
}

/* De volledige bewoording: voor keuzelijsten, toelichtingen en alles wat de
   houder onder ogen krijgt. Bij een vergunning-subdoel is er geen langere
   vorm; dan is het korte label meteen het hele label. */
export function doelVoluit(value) {
  const d = MELDING_DOELEN.find((o) => o.waarde === value);
  if (d) return d.voluit;
  return vergunningLabel(value) || value;
}

/* Wat je in een tabelcel tekent. `beide` ís grasland én gewas, en dat leest in
   een lijst beter als twee chips dan als één lange regel. De filterwaarde
   blijft in allebei de gevallen het doel zelf, dus klikken op een van de twee
   filtert nog steeds op `beide`. */
export function chipsVoorDoel(value) {
  const stuk = (v) => ({ label: labelVoorDoel(v), titel: doelVoluit(v) });
  if (value === 'beide') return [stuk('grasland'), stuk('gewas')];
  return [stuk(value)];
}

/* Welke categorieën zijn aangevinkt, gegeven de opgeslagen doelen? Nodig om een
   bestaande vergunning terug in het formulier te kunnen zetten: daar staan de
   sub-doelen in, niet de categorieën. */
export function categorieenVanDoelen(doelen) {
  if (!doelen || doelen.length === 0) return [];
  return VERGUNNING_CATEGORIEEN
    .filter((cat) => cat.opties.some((opt) => doelen.includes(opt.value)))
    .map((cat) => cat.key);
}
